import { useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Spinner } from '../ui/Spinner';
import { tauriKeyStorage } from '../../services/assistant/keyStorage';
import { keySession } from '../../services/assistant/keySession';
import { useNovaStore } from '../../store/useNovaStore';

interface Props {
  onClose: () => void;
}

export function UnlockKeyModal({ onClose }: Props) {
  const setKeyUnlocked = useNovaStore((s) => s.setKeyUnlocked);
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  // Focus the modal on open
  useEffect(() => {
    dialogRef.current?.focus();
    passwordRef.current?.focus();
  }, []);

  // Escape closes the modal
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  async function handleUnlock() {
    setError(null);
    setLoading(true);
    try {
      const file = await tauriKeyStorage.readKeyFile();
      // Focus password after file is selected
      passwordRef.current?.focus();
      await keySession.unlock(file, password);
      setKeyUnlocked(true);
      onClose();
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      if (msg === 'no-key-selected') {
        setError('Aucune clé sélectionnée');
      } else if (msg === 'bad-password') {
        setError('Mot de passe incorrect');
      } else {
        setError("Impossible de déverrouiller la clé. Vérifiez le fichier et le mot de passe.");
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4"
      onClick={onClose}
      aria-modal="true"
      role="dialog"
      aria-label="Déverrouiller la clé CIME"
    >
      <div
        ref={dialogRef}
        tabIndex={-1}
        className="bg-white rounded-2xl shadow-2xl border border-cime-border w-full max-w-sm p-6 focus:outline-none"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading text-lg font-semibold text-cime-text-primary">
            Déverrouiller la clé CIME
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fermer"
            className="text-cime-text-muted hover:text-cime-text-primary transition"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <p className="text-sm text-cime-text-muted mb-4">
          Sélectionnez votre fichier <code className="font-mono text-xs bg-neutral-100 px-1 py-0.5 rounded">cime-key.json</code> sur votre clé USB, puis saisissez votre mot de passe.
        </p>

        <div className="space-y-3">
          <div>
            <label className="block text-xs uppercase tracking-wider font-semibold text-neutral-500 mb-1.5">
              Mot de passe
            </label>
            <Input
              ref={passwordRef}
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleUnlock();
              }}
              placeholder="Mot de passe de la clé"
              autoComplete="current-password"
            />
          </div>

          {error && (
            <p className="text-sm text-red-600" role="alert">
              {error}
            </p>
          )}

          <Button
            onClick={handleUnlock}
            disabled={loading || !password}
            className="w-full"
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <Spinner className="w-4 h-4" /> Déverrouillage…
              </span>
            ) : (
              "Sélectionner la clé et déverrouiller"
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
